import type { CalendarDate } from "@internationalized/date";
import { nearbyAirportsOptions } from "@jetblue/dotcom-api/odb/nearby-airports";
import { today } from "@internationalized/date";
import { transformedDataOptions } from "@jetblue/dotcom-api/odb/transformed";
import type { SyntheticEvent } from "react";
import { useCallback, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { FlightSearchFormLabels } from "../labels";
import type { UseFormProperties } from "../use-form";
import { DEFAULT_LABELS } from "../labels";
import { flightSearchFormHasErrors } from "./use-flight-search-form/validation";
import { useForm } from "../use-form";
import { usePassengers } from "../use-passengers";
import { useStateLocalStorage } from "./use-localstorage-state";
import { TIMEZONE } from "../timezone";

export type FlightSearchFormConfig = {
  bookingUrl: string;
  calendarLookAheadCount: number;
  showDatePickerPricing: boolean;
  showPromoCode: boolean;
};
export type TripType = "one-way" | "round-trip" | "multi-city";
export type UseFlightSearchFormProperties = {
  config?: FlightSearchFormConfig | undefined;
  initialRoutes?: UseFormProperties["initialRoutes"];
  labels?: Partial<FlightSearchFormLabels>;
  origin: string;
  baseUrl?: string | undefined;
  startDate?: CalendarDate | undefined;
  endDate?: CalendarDate | undefined;
};
export const useFlightSearchForm = ({
  baseUrl = "",
  config,
  endDate,
  initialRoutes,
  labels,
  origin,
  startDate,
}: UseFlightSearchFormProperties) => {
  const formReference = useRef<HTMLFormElement>(null);
  const passengers = usePassengers();
  const formList = useForm({
    endDate:
      endDate ??
      today(TIMEZONE).add({
        days: config?.calendarLookAheadCount,
      }),
    initialRoutes,
    maxRoutes: 4,
    minRoutes: 2,
    startDate,
  });

  const [tripType, setTripType] = useStateLocalStorage<TripType>(
    "trip-type",
    "round-trip",
  );
  const onTripTypeChange = useCallback((value: TripType | null) => {
    setTripType((current) => value ?? current);
  }, []);
  const odQuery = useQuery(transformedDataOptions(origin, baseUrl));
  const nearbyAirports = useQuery(nearbyAirportsOptions());

  const [showValidation, setShowValidation] = useState(false);
  const hasErrors = flightSearchFormHasErrors({ formList, tripType });
  const handleSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>) => {
      if (hasErrors) {
        event.preventDefault();

        if (!showValidation) {
          setShowValidation(true);
        }
        //////////////////////////////////////////////////////////////
        // Please try anything but this before doing this           //
        //////////////////////////////////////////////////////////////
        requestAnimationFrame(() => {
          formReference.current
            ?.querySelector<HTMLInputElement>("input[aria-invalid='true']")
            ?.focus();
        });
        //////////////////////////////////////////////////////////////
      }
    },
    [hasErrors, formReference, showValidation, setShowValidation],
  );

  return {
    bookingUrl: config?.bookingUrl,
    errors: {
      hasErrors,
      setShowValidation,
      showValidation,
    },
    formList,
    formReference,
    handleSubmit,
    labels: {
      ...DEFAULT_LABELS,
      ...labels,
      datePickerToolbar: {
        ...DEFAULT_LABELS.datePickerToolbar,
        ...labels?.datePickerToolbar,
      },
      passengersDropdown: {
        ...DEFAULT_LABELS.passengersDropdown,
        ...labels?.passengersDropdown,
      },
    } satisfies FlightSearchFormLabels,
    nearbyAirports,
    odQuery,
    onTripTypeChange,
    passengers,
    showDatePickerPricing: config?.showDatePickerPricing,
    showPromoCode: config?.showPromoCode,
    tripType,
  };
};
